import React from "react";
import { useContent } from "@thoughtbot/superglue";

import { Form, type FormProps } from "@javascript/components/Inputs";
import { Badge } from "@javascript/components/ui/badge";
import { Button } from "@javascript/components/ui/button";
import { TypographyH1, TypographyMuted } from "@javascript/components/ui/typography";
import { cn } from "@javascript/components/utils";

const CATEGORY_ALL = "All";
const VISIBLE_THEME_CAP = 9;

type Theme = {
  key: string;
  name: string;
  category: string | null;
  previewUrl: string;
  demoUrl: string;
  selected: boolean;
  input: Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    "id" | "name" | "value" | "defaultChecked"
  >;
};

type ThemePageContent = {
  invitationName: string;
  currentThemeKey: string;
  dashboardPath: string;
  changeThemeForm: FormProps;
  themes: Theme[];
};

export default function InvitationTheme() {
  const { invitationName, currentThemeKey, dashboardPath, changeThemeForm, themes } =
    useContent<ThemePageContent>();
  const [selectedCategory, setSelectedCategory] = React.useState(CATEGORY_ALL);
  const [selectedThemeKey, setSelectedThemeKey] = React.useState(currentThemeKey);
  const [showAllThemes, setShowAllThemes] = React.useState(false);
  const categories = React.useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(
        themes
          .map((theme) => theme.category)
          .filter((category): category is string => Boolean(category)),
      ),
    );

    return uniqueCategories.length > 1 ? [ CATEGORY_ALL, ...uniqueCategories ] : [];
  }, [ themes ]);
  const filteredThemes = selectedCategory === CATEGORY_ALL
    ? themes
    : themes.filter((theme) => theme.category === selectedCategory);
  const visibleThemes = showAllThemes ? filteredThemes : filteredThemes.slice(0, VISIBLE_THEME_CAP);
  const hiddenThemeCount = filteredThemes.length - visibleThemes.length;

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setShowAllThemes(false);
  };

  return (
    <div className="p-6">
      <section className="mx-auto w-full max-w-5xl rounded-lg border bg-background p-6 shadow-sm">
        <TypographyH1>Change theme</TypographyH1>
        <TypographyMuted className="mt-2">
          Changing theme for {invitationName}. Your invitation details will stay the same.
        </TypographyMuted>
        <Form
          {...changeThemeForm.form}
          extras={changeThemeForm.extras}
          data-sg-visit="true"
          className="mt-6 space-y-5"
        >
          <fieldset>
            <legend className="text-base font-medium">Choose a theme</legend>
            {categories.length > 0 && (
              <div
                role="group"
                aria-label="Filter themes by category"
                className="mt-4 flex flex-wrap gap-2"
              >
                {categories.map((category) => (
                  <Button
                    key={category}
                    type="button"
                    size="sm"
                    variant={selectedCategory === category ? "default" : "outline"}
                    aria-pressed={selectedCategory === category}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {visibleThemes.map((theme) => (
                <div key={theme.key} data-theme-option className="min-w-0 space-y-2">
                  <label
                    htmlFor={theme.input.id}
                    className="flex cursor-pointer flex-col overflow-hidden rounded-lg border bg-background transition-colors hover:border-primary/50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50 has-[:checked]:border-primary has-[:checked]:ring-2 has-[:checked]:ring-primary/25"
                  >
                    <img
                      src={theme.previewUrl}
                      alt={`${theme.name} preview`}
                      className="aspect-[3/4] w-full object-cover"
                    />
                    <span className="flex items-center justify-center gap-1.5 border-t px-2.5 py-2">
                      <input
                        {...theme.input}
                        type="radio"
                        className="size-4 shrink-0 cursor-pointer accent-primary"
                        onChange={() => setSelectedThemeKey(theme.key)}
                      />
                      <span className="truncate text-sm font-medium">{theme.name}</span>
                    </span>
                  </label>
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href={theme.demoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-muted-foreground underline underline-offset-4 transition-colors hover:text-primary"
                    >
                      View live demo
                    </a>
                    {theme.key === currentThemeKey ? (
                      <Badge variant="secondary">Current theme</Badge>
                    ) : theme.key === selectedThemeKey ? (
                      <Badge variant="outline">Selected</Badge>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {hiddenThemeCount > 0 && (
              <div className="mt-4">
                <Button type="button" variant="outline" onClick={() => setShowAllThemes(true)}>
                  Show all {filteredThemes.length} themes
                </Button>
              </div>
            )}
          </fieldset>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button asChild type="button" variant="outline">
              <a href={dashboardPath} data-sg-visit="true">Cancel</a>
            </Button>
            <Button type="submit">Save theme</Button>
          </div>
        </Form>
      </section>
    </div>
  );
}
